import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import XPHeader from "@/components/XPHeader";
import DecodeMiniCard from "@/components/DecodeMiniCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { CheckCircle2, Circle, Sparkles, ArrowRight, Loader2 } from "lucide-react";
import { RadarChart, PolarGrid, PolarAngleAxis, Radar, ResponsiveContainer } from "recharts";
import { toast } from "sonner";

export default function Dashboard() {
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [data, setData] = useState(null);
  const [word, setWord] = useState("");
  const [quick, setQuick] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try { const r = await api.get("/dashboard"); setData(r.data); } catch (e) { /* ignore */ }
  };
  useEffect(() => { load(); }, []);

  const quickDecode = async () => {
    if (!word.trim()) return;
    setBusy(true);
    try {
      const r = await api.post("/decode/mini", { text: word, decode_type: "Personal Identity" });
      setQuick(r.data);
      await Promise.all([load(), refresh()]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Decode failed");
    } finally { setBusy(false); }
  };

  if (!data) {
    return <div className="space-y-4"><Skeleton className="h-28 w-full bg-white/[0.04]" /><Skeleton className="h-64 w-full bg-white/[0.04]" /></div>;
  }

  const radarData = Object.entries(data.stats || {}).map(([k, v]) => ({ stat: (data.stat_labels?.[k] || k).split(" ")[0], value: v }));

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-2xl font-display font-semibold">Welcome back, {data.user?.username}</h1>
        <p className="text-[var(--bone-muted)] text-sm">Build your language intelligence today.</p>
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 space-y-4">
          <XPHeader progress={data.progress} streak={data.streak} />

          {/* Quick decode */}
          <div className="litra-card p-5">
            <div className="text-sm font-display font-semibold mb-2">Quick Decode</div>
            <div className="flex gap-2">
              <Input value={word} onChange={(e) => setWord(e.target.value)} placeholder="Decode a name, brand, or word…"
                className="bg-white/[0.04] border-white/[0.1]" data-testid="dashboard-quick-decode-input"
                onKeyDown={(e) => e.key === "Enter" && quickDecode()} />
              <Button onClick={quickDecode} disabled={busy} className="bg-[var(--primary)] hover:opacity-90" data-testid="dashboard-quick-decode-button">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Decode"}
              </Button>
            </div>
            {quick && <div className="mt-4"><DecodeMiniCard data={quick} decodeType="Personal Identity" /></div>}
            <div className="mt-3 text-xs text-[var(--bone-faint)]">{data.limits_remaining?.mini_decodes >= 999999 ? "Unlimited mini-decodes" : `${data.limits_remaining?.mini_decodes} mini-decodes left today`}</div>
          </div>

          {/* Daily challenge */}
          <div className="litra-card p-5" data-testid="dashboard-daily-challenge">
            <div className="flex items-center justify-between">
              <div className="text-sm font-display font-semibold">Daily Challenge</div>
              <Badge className="gold-edge gold-text bg-transparent"><Sparkles className="w-3 h-3 mr-1" />+{data.daily_challenge?.reward_xp} XP</Badge>
            </div>
            <div className="mt-2 text-[var(--bone-muted)] text-sm">Decode <span className="text-[var(--bone)] font-semibold">{data.daily_challenge?.word}</span> — {data.daily_challenge?.mini?.overview}</div>
            <Button variant="outline" className="mt-3 glass border-[rgba(185,192,204,0.25)]" data-testid="dashboard-daily-decode-button"
              onClick={() => { setWord(data.daily_challenge?.word); setTimeout(quickDecode, 50); }}>
              Decode {data.daily_challenge?.word} <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </div>

        <div className="space-y-4">
          {/* Stats radar */}
          <div className="litra-card p-4">
            <div className="text-sm font-display font-semibold mb-1">Player Stats</div>
            <div className="h-56">
              <ResponsiveContainer width="100%" height="100%">
                <RadarChart data={radarData} outerRadius="72%">
                  <PolarGrid stroke="rgba(185,192,204,0.15)" />
                  <PolarAngleAxis dataKey="stat" tick={{ fill: "#A9A39A", fontSize: 9 }} />
                  <Radar dataKey="value" stroke="#2E9E76" fill="#145A43" fillOpacity={0.45} />
                </RadarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Plan */}
          <div className="litra-card p-5">
            <div className="flex items-center justify-between">
              <div className="text-sm font-display font-semibold">{data.plan?.name} Plan</div>
              <Badge variant="outline" className="green-text border-[rgba(20,90,67,0.5)]">{data.user?.credits} credits</Badge>
            </div>
            <Button variant="outline" className="w-full mt-3 glass border-[rgba(185,192,204,0.25)]" onClick={() => navigate("/pricing")} data-testid="dashboard-upgrade-button">
              {data.plan?.id === "free" ? "Upgrade" : "Manage plan"}
            </Button>
          </div>
        </div>
      </div>

      {/* Missions */}
      <div className="litra-card p-5">
        <div className="flex items-center justify-between mb-3">
          <div className="text-sm font-display font-semibold">Missions</div>
          <span className="text-xs text-[var(--bone-faint)]">{data.missions?.filter((m) => m.completed).length}/{data.missions?.length} complete</span>
        </div>
        <div className="grid sm:grid-cols-2 gap-2" data-testid="dashboard-missions">
          {data.missions?.map((m) => (
            <div key={m.id} className="flex items-start gap-3 p-3 rounded-xl bg-white/[0.025] border border-white/[0.06]">
              {m.completed ? <CheckCircle2 className="w-4 h-4 green-text mt-0.5 shrink-0" /> : <Circle className="w-4 h-4 text-[var(--bone-faint)] mt-0.5 shrink-0" />}
              <div>
                <div className={`text-sm ${m.completed ? "text-[var(--bone)]" : "text-[var(--bone-muted)]"}`}>{m.title}</div>
                <div className="text-xs text-[var(--bone-faint)]">{m.description} · +{m.reward_xp} XP{m.reward_credits ? ` · +${m.reward_credits} credit` : ""}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Recent */}
      {data.recent_decodes?.length > 0 && (
        <div className="litra-card p-5">
          <div className="text-sm font-display font-semibold mb-3">Recent decodes</div>
          <div className="flex flex-wrap gap-2">
            {data.recent_decodes.map((r) => (
              <button key={r.id} onClick={() => navigate("/decode")} className="glass rounded-full px-3 py-1.5 text-sm hover:border-[rgba(20,90,67,0.5)]">
                {r.input_text} <span className="text-[var(--bone-faint)] text-xs">· {r.word_class}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
